import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import VectorLayer from "ol/layer/Vector";
import VectorTileLayer from "ol/layer/VectorTile";
import VectorSource from "ol/source/Vector";
import VectorTileSource from "ol/source/VectorTile";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat } from "ol/proj";
import MVT from "ol/format/MVT";
import Style from "ol/style/Style";
import { Circle as CircleStyle, Fill, Stroke, Icon as IconStyle } from "ol/style";
import { buildApiUrl } from "../utils/apiConfig";

const RES0 = 156543.03392804097;
const clamp = (min, value, max) => Math.max(min, Math.min(value, max));
const lockStyleCacheRef = { current: {} };
const dockStyleCacheRef = { current: {} };
const pegelStyleCacheRef = { current: {} };
const worldHeritageStyleCacheRef = { current: {} };

const normalizeNumericId = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

function scaleFromResolution(resolution) {
  const zoom = Math.max(0, Math.round(Math.log2(RES0 / resolution)));
  return clamp(0.6, 0.7 + (zoom - 6) * 0.09, 1.8);
}

const lockStyle = (feature, resolution) => {
  const selected = !!feature.get("selected");
  const zoom = Math.max(0, Math.round(Math.log2(RES0 / resolution)));
  const key = `${zoom}-${selected ? 1 : 0}`;
  const cache = lockStyleCacheRef.current;
  if (cache[key]) return cache[key];

  const s = scaleFromResolution(resolution);

  const halo = new Style({
    image: new CircleStyle({
      radius: 10 * s,
      fill: new Fill({ color: "rgba(255,255,255,0.92)" }),
      stroke: new Stroke({
        color: selected ? "#ff5a00" : "rgba(0,0,0,0.25)",
        width: selected ? 3 : 2,
      }),
    }),
    zIndex: 0,
  });

  const icon = new Style({
    image: new IconStyle({
      src:
        "data:image/svg+xml;charset=utf-8," +
        encodeURIComponent(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM15.1 8H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill="#666"/>
        </svg>
      `),
      anchor: [0.5, 1],
      anchorXUnits: "fraction",
      anchorYUnits: "fraction",
      scale: s,
    }),
    zIndex: 1,
  });

  cache[key] = [halo, icon];
  return cache[key];
};

const dockStyle = (feature, resolution) => {
  const selected = !!feature.get("selected");
  const zoom = Math.max(0, Math.round(Math.log2(RES0 / resolution)));
  const key = `${zoom}-${selected ? 1 : 0}`;
  const cache = dockStyleCacheRef.current;
  if (cache[key]) return cache[key];

  const s = scaleFromResolution(resolution);

  const halo = new Style({
    image: new CircleStyle({
      radius: 10 * s,
      fill: new Fill({ color: "rgba(255,255,255,0.92)" }),
      stroke: new Stroke({
        color: selected ? "#ff5a00" : "rgba(0,0,0,0.25)",
        width: selected ? 3 : 2,
      }),
    }),
    zIndex: 0,
  });

  const icon = new Style({
    image: new IconStyle({
      src:
        "data:image/svg+xml;charset=utf-8," +
        encodeURIComponent(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#2196F3"/>
          <path d="M12 24l-2-2h4l-2 2z" fill="#2196F3"/>
        </svg>
      `),
      anchor: [0.5, 1],
      anchorXUnits: "fraction",
      anchorYUnits: "fraction",
      scale: s,
    }),
    zIndex: 1,
  });

  cache[key] = [halo, icon];
  return cache[key];
};

const pegelStyle = (feature, resolution) => {
  const selected = !!feature.get("selected");
  const zoom = Math.max(0, Math.round(Math.log2(RES0 / resolution)));
  const key = `${zoom}-${selected ? 1 : 0}`;
  const cache = pegelStyleCacheRef.current;
  if (cache[key]) return cache[key];

  const s = scaleFromResolution(resolution);

  const halo = new Style({
    image: new CircleStyle({
      radius: 10 * s,
      fill: new Fill({ color: "rgba(255,255,255,0.92)" }),
      stroke: new Stroke({
        color: selected ? "#ff5a00" : "rgba(0,0,0,0.25)",
        width: selected ? 3 : 2,
      }),
    }),
    zIndex: 0,
  });

  const icon = new Style({
    image: new IconStyle({
      src:
        "data:image/svg+xml;charset=utf-8," +
        encodeURIComponent(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 4 4.5 10.5 6.5 13 .3.4.9.4 1.2 0 2-2.5 6.3-9 6.3-13 0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#1976D2"/>
          <path d="M7 19h10v2H7z" fill="#1976D2"/>
          <path d="M6 21h12v2H6z" fill="#1976D2"/>
        </svg>
      `),
      anchor: [0.5, 1],
      anchorXUnits: "fraction",
      anchorYUnits: "fraction",
      scale: s,
    }),
    zIndex: 1,
  });

  cache[key] = [halo, icon];
  return cache[key];
};

const worldHeritageStyle = (feature, resolution, activeSelection = null) => {
  const featureKind = String(feature.get?.("feature_kind") || "site").toLowerCase() === "component"
    ? "component"
    : "site";
  const featureId = normalizeNumericId(feature.get?.("id"));
  const parentSiteId =
    normalizeNumericId(feature.get?.("parent_site_id")) ||
    (featureKind === "site" ? featureId : null);
  const activeParentSiteId = normalizeNumericId(activeSelection?.parentSiteId);
  const activeSiteId = normalizeNumericId(activeSelection?.siteId);
  const activeComponentId = normalizeNumericId(activeSelection?.componentId);
  const parentHighlighted =
    activeParentSiteId !== null && parentSiteId !== null && activeParentSiteId === parentSiteId;
  const selfSelected =
    (featureKind === "site" && activeSiteId !== null && featureId !== null && activeSiteId === featureId) ||
    (featureKind === "component" &&
      activeComponentId !== null &&
      featureId !== null &&
      activeComponentId === featureId);
  const categoryRaw = String(feature.get?.("category") || "").toLowerCase();
  const zoom = Math.max(0, Math.round(Math.log2(RES0 / resolution)));
  const cache = worldHeritageStyleCacheRef.current;
  const s = scaleFromResolution(resolution);

  if (featureKind === "component") {
    const key = `${zoom}-component-${parentHighlighted ? 1 : 0}-${selfSelected ? 1 : 0}`;
    if (cache[key]) return cache[key];

    let fillColor = "#3a7bd5";
    if (parentHighlighted) fillColor = "#2e86ff";
    if (selfSelected) fillColor = "#ff5a00";

    const styles = [];
    const accentColor = selfSelected ? "#ff5a00" : "#ffd166";

    if (parentHighlighted || selfSelected) {
      styles.push(
        new Style({
          image: new CircleStyle({
            radius: (selfSelected ? 10.5 : 8.8) * s,
            fill: new Fill({
              color: selfSelected ? "rgba(255,90,0,0.28)" : "rgba(255,209,102,0.26)",
            }),
            stroke: new Stroke({
              color: selfSelected ? "rgba(255,90,0,0.95)" : "rgba(255,209,102,0.95)",
              width: selfSelected ? 3.0 : 2.6,
            }),
          }),
          zIndex: selfSelected ? 9 : 7,
        })
      );

      styles.push(
        new Style({
          image: new CircleStyle({
            radius: (selfSelected ? 7.0 : 6.0) * s,
            fill: new Fill({ color: "rgba(255,255,255,0.98)" }),
            stroke: new Stroke({
              color: accentColor,
              width: selfSelected ? 2.6 : 2.2,
            }),
          }),
          zIndex: selfSelected ? 10 : 8,
        })
      );
    } else {
      styles.push(
        new Style({
          image: new CircleStyle({
            radius: 5.6 * s,
            fill: new Fill({ color: "rgba(255,255,255,0.95)" }),
            stroke: new Stroke({
              color: "rgba(0,0,0,0.22)",
              width: 1.2,
            }),
          }),
          zIndex: 2,
        })
      );
    }

    styles.push(
      new Style({
        image: new CircleStyle({
          radius: (selfSelected ? 5.0 : parentHighlighted ? 4.6 : 4.2) * s,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({
            color: selfSelected ? "#1a1a1a" : "#ffffff",
            width: selfSelected ? 2.0 : parentHighlighted ? 1.8 : 1.5,
          }),
        }),
        zIndex: selfSelected ? 11 : parentHighlighted ? 9 : 3,
      })
    );

    cache[key] = styles;
    return styles;
  }

  let fillColor = "#7d8896"; // fallback
  if (categoryRaw === "cultural") fillColor = "#c04a36";
  else if (categoryRaw === "natural") fillColor = "#2f8f5b";
  else if (categoryRaw === "mixed") fillColor = "#9a6f1f";

  const selected = selfSelected || parentHighlighted;
  const key = `${zoom}-site-${selected ? 1 : 0}-${selfSelected ? 1 : 0}-${categoryRaw || "unknown"}`;
  if (cache[key]) return cache[key];

  const styles = [];
  const accentColor = selfSelected ? "#ff5a00" : "#ffd166";

  if (selected) {
    styles.push(
      new Style({
        image: new CircleStyle({
          radius: (selfSelected ? 14.5 : 12.0) * s,
          fill: new Fill({
            color: selfSelected ? "rgba(255,90,0,0.24)" : "rgba(255,209,102,0.22)",
          }),
          stroke: new Stroke({
            color: selfSelected ? "rgba(255,90,0,0.98)" : "rgba(255,209,102,0.98)",
            width: selfSelected ? 3.6 : 3.0,
          }),
        }),
        zIndex: selfSelected ? 12 : 9,
      })
    );

    styles.push(
      new Style({
        image: new CircleStyle({
          radius: (selfSelected ? 10.2 : 8.8) * s,
          fill: new Fill({ color: "rgba(255,255,255,0.98)" }),
          stroke: new Stroke({
            color: selfSelected ? "#1a1a1a" : accentColor,
            width: selfSelected ? 2.4 : 2.6,
          }),
        }),
        zIndex: selfSelected ? 13 : 10,
      })
    );
  } else {
    styles.push(
      new Style({
        image: new CircleStyle({
          radius: 8.8 * s,
          fill: new Fill({ color: "rgba(255,255,255,0.94)" }),
          stroke: new Stroke({
            color: "rgba(0,0,0,0.24)",
            width: 1.4,
          }),
        }),
        zIndex: 3,
      })
    );
  }

  styles.push(
    new Style({
      image: new CircleStyle({
        radius: (selfSelected ? 8.1 : selected ? 7.2 : 6.8) * s,
        fill: new Fill({ color: selfSelected ? "#ff5a00" : fillColor }),
        stroke: new Stroke({
          color: selfSelected ? "#ffffff" : "#ffffff",
          width: selfSelected ? 2.4 : selected ? 2.0 : 1.8,
        }),
      }),
      zIndex: selfSelected ? 14 : selected ? 11 : 4,
    })
  );

  if (selfSelected) {
    styles.push(
      new Style({
        image: new CircleStyle({
          radius: 2.4 * s,
          fill: new Fill({ color: "rgba(255,255,255,1)" }),
          stroke: new Stroke({
            color: "rgba(0,0,0,0.25)",
            width: 1.0,
          }),
        }),
        zIndex: 15,
      })
    );
  }

  cache[key] = styles;
  return styles;
};

export default function useShipLayersController({ getMap, getApiHeaders, getTileAuthQuery }) {
  const docksLayerRef = useRef(null);
  const docksSourceRef = useRef(null);
  const locksLayerRef = useRef(null);
  const locksSourceRef = useRef(null);
  const pegelsLayerRef = useRef(null);
  const pegelsSourceRef = useRef(null);
  const worldHeritageLayerRef = useRef(null);
  const worldHeritageSourceRef = useRef(null);
  const worldHeritageActiveSelectionRef = useRef({
    parentSiteId: null,
    siteId: null,
    componentId: null,
  });

  const [isLocks, setIsLocks] = useState(false);
  const [isDockingPoints, setIsDockingPoints] = useState(false);
  const [isPegelsVisible, setIsPegelsVisible] = useState(false);
  const [isWorldHeritageSitesVisible, setIsWorldHeritageSitesVisible] = useState(false);
  const [availablePegels, setAvailablePegels] = useState([]);

  const ensureLayer = useCallback(
    (layerRef, sourceRef, zIndex = 50, layerOptions = {}) => {
      const map = getMap?.();
      if (!map) return null;
      if (!sourceRef.current) sourceRef.current = new VectorSource();

      if (!layerRef.current) {
        layerRef.current = new VectorLayer({
          source: sourceRef.current,
          zIndex,
          ...layerOptions,
        });
        map.addLayer(layerRef.current);
      } else {
        if (layerOptions.style) layerRef.current.setStyle(layerOptions.style);
        if (typeof zIndex === "number") layerRef.current.setZIndex(zIndex);
      }

      return layerRef.current;
    },
    [getMap]
  );

  const ensureWorldHeritageTileLayer = useCallback(() => {
    const map = getMap?.();
    if (!map) return null;

    if (!worldHeritageSourceRef.current) {
      const tileAuthQuery = getTileAuthQuery?.();
      const url = buildApiUrl(
        `tiles/world_heritage_sites/{z}/{x}/{y}.pbf${tileAuthQuery ? `?${tileAuthQuery}` : ""}`
      );
      worldHeritageSourceRef.current = new VectorTileSource({
        format: new MVT(),
        url,
        minZoom: 2,
        maxZoom: 18,
        cacheSize: 256,
      });
    }

    const worldHeritageTileStyle = (feature, resolution) =>
      worldHeritageStyle(feature, resolution, worldHeritageActiveSelectionRef.current);

    if (!worldHeritageLayerRef.current) {
      worldHeritageLayerRef.current = new VectorTileLayer({
        source: worldHeritageSourceRef.current,
        zIndex: 44,
        visible: false,
        declutter: false,
        style: worldHeritageTileStyle,
        minZoom: 2,
        maxZoom: 18,
        preload: 0,
        useInterimTilesOnError: false,
      });
      map.addLayer(worldHeritageLayerRef.current);
    }

    return worldHeritageLayerRef.current;
  }, [getMap, getTileAuthQuery]);

  const setWorldHeritageActiveSelection = useCallback((selection = {}) => {
    const next = {
      parentSiteId: normalizeNumericId(selection.parentSiteId),
      siteId: normalizeNumericId(selection.siteId),
      componentId: normalizeNumericId(selection.componentId),
    };
    const current = worldHeritageActiveSelectionRef.current;
    const changed =
      current.parentSiteId !== next.parentSiteId ||
      current.siteId !== next.siteId ||
      current.componentId !== next.componentId;
    if (!changed) return;
    worldHeritageActiveSelectionRef.current = next;
    if (worldHeritageLayerRef.current) {
      worldHeritageLayerRef.current.changed();
    }
  }, []);

  const clearWorldHeritageActiveSelection = useCallback(() => {
    setWorldHeritageActiveSelection({
      parentSiteId: null,
      siteId: null,
      componentId: null,
    });
  }, [setWorldHeritageActiveSelection]);

  const clearLayer = useCallback((sourceRef) => {
    if (sourceRef.current) sourceRef.current.clear();
  }, []);

  const showDocks = useCallback(async () => {
    const map = getMap?.();
    if (!map) return;

    ensureLayer(docksLayerRef, docksSourceRef, 42, {
      declutter: true,
      style: dockStyle,
    });

    try {
      const res = await axios.get(buildApiUrl("dockingpoints/"), {
        headers: await getApiHeaders(),
      });
      clearLayer(docksSourceRef);

      (res.data || []).forEach((dock) => {
        let coord = null;
        if (Array.isArray(dock?.coord) && dock.coord.length >= 2) {
          const lat = Number(dock.coord[0]);
          const lon = Number(dock.coord[1]);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            coord = [lon, lat];
          }
        }
        if (!coord && Array.isArray(dock?.location?.coordinates) && dock.location.coordinates.length >= 2) {
          const lon = Number(dock.location.coordinates[0]);
          const lat = Number(dock.location.coordinates[1]);
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            coord = [lon, lat];
          }
        }
        if (!coord) return;

        const feat = new Feature({
          geometry: new Point(fromLonLat(coord, map.getView().getProjection())),
          kind: "dock",
          name: dock.full_name,
          selected: false,
          dockData: dock,
        });
        docksSourceRef.current.addFeature(feat);
      });
      setIsDockingPoints(true);
    } catch (error) {
      console.error("Error fetching docking points:", error);
    }
  }, [clearLayer, ensureLayer, getApiHeaders, getMap]);

  const toggleDocks = useCallback(async () => {
    if (isDockingPoints) {
      clearLayer(docksSourceRef);
      setIsDockingPoints(false);
      return;
    }
    await showDocks();
  }, [clearLayer, isDockingPoints, showDocks]);

  const showLocks = useCallback(async () => {
    const map = getMap?.();
    if (!map) return;

    ensureLayer(locksLayerRef, locksSourceRef, 42, {
      declutter: true,
      style: lockStyle,
    });

    try {
      const res = await axios({
        url: buildApiUrl("list_locks/"),
        method: "POST",
        data: { cruise: "ams_bus" },
        headers: await getApiHeaders(),
      });

      clearLayer(locksSourceRef);
      const allLocks = res.data?.locks || [];
      const list = allLocks.filter(
        (lock) => lock.segment_id !== 24316 && lock.lock?.segment_id !== 24316
      );

      list.forEach((lock) => {
        const [lon, lat] = lock.lock.geometry.coordinates;
        const feat = new Feature({
          geometry: new Point(fromLonLat([lon, lat], map.getView().getProjection())),
          kind: "lock",
          name: lock.lock.name,
          tags: lock.lock.tags,
          altitude: lock.lock.altitude,
          waterway_distance: lock.lock.waterway_distance,
          selected: false,
          lockData: lock.lock,
          fullData: lock,
        });
        locksSourceRef.current.addFeature(feat);
      });

      setIsLocks(list.length > 0);
    } catch (error) {
      console.error(error);
    }
  }, [clearLayer, ensureLayer, getApiHeaders, getMap]);

  const toggleLocks = useCallback(async () => {
    if (isLocks) {
      clearLayer(locksSourceRef);
      setIsLocks(false);
      return;
    }
    await showLocks();
  }, [clearLayer, isLocks, showLocks]);

  const showPegels = useCallback(async () => {
    const map = getMap?.();
    if (!map) return;

    ensureLayer(pegelsLayerRef, pegelsSourceRef, 45, {
      declutter: true,
      style: pegelStyle,
    });

    try {
      const res = await axios.get(buildApiUrl("pegel_locations/"), {
        headers: await getApiHeaders(),
      });

      clearLayer(pegelsSourceRef);
      const pegelsList = (res.data.features || []).map((pegelFeature) => ({
        pegel_id: pegelFeature.properties.pegel_id,
        name:
          pegelFeature.properties.longname ||
          pegelFeature.properties.shortname ||
          `Pegel ${pegelFeature.properties.pegel_id}`,
        shortname: pegelFeature.properties.shortname,
      }));
      setAvailablePegels(pegelsList);

      (res.data.features || []).forEach((pegelFeature) => {
        const coords = pegelFeature?.geometry?.coordinates;
        const props = pegelFeature?.properties;
        if (!coords || coords.length < 2 || !props) return;

        const feat = new Feature({
          geometry: new Point(fromLonLat(coords, map.getView().getProjection())),
          kind: "pegel",
          name: props.longname || props.shortname || `Pegel ${props.pegel_id}`,
          selected: false,
          pegelData: props,
        });
        pegelsSourceRef.current.addFeature(feat);
      });

      setIsPegelsVisible(true);
    } catch (error) {
      console.error("Error fetching pegels:", error);
    }
  }, [clearLayer, ensureLayer, getApiHeaders, getMap]);

  const togglePegels = useCallback(async () => {
    if (isPegelsVisible) {
      clearLayer(pegelsSourceRef);
      setIsPegelsVisible(false);
      setAvailablePegels([]);
      return;
    }
    await showPegels();
  }, [clearLayer, isPegelsVisible, showPegels]);

  const showWorldHeritageSites = useCallback(async () => {
    const layer = ensureWorldHeritageTileLayer();
    if (!layer) return;
    layer.setVisible(true);
    setIsWorldHeritageSitesVisible(true);
  }, [ensureWorldHeritageTileLayer]);

  const toggleWorldHeritageSites = useCallback(async () => {
    if (isWorldHeritageSitesVisible) {
      if (worldHeritageLayerRef.current) {
        worldHeritageLayerRef.current.setVisible(false);
      }
      clearWorldHeritageActiveSelection();
      setIsWorldHeritageSitesVisible(false);
      return;
    }
    await showWorldHeritageSites();
  }, [clearWorldHeritageActiveSelection, isWorldHeritageSitesVisible, showWorldHeritageSites]);

  const clearAll = useCallback(() => {
    clearLayer(locksSourceRef);
    clearLayer(docksSourceRef);
    clearLayer(pegelsSourceRef);
    if (worldHeritageLayerRef.current) {
      worldHeritageLayerRef.current.setVisible(false);
    }
    clearWorldHeritageActiveSelection();
    setIsLocks(false);
    setIsDockingPoints(false);
    setIsPegelsVisible(false);
    setIsWorldHeritageSitesVisible(false);
    setAvailablePegels([]);
  }, [clearLayer, clearWorldHeritageActiveSelection]);

  const getLayerRefs = useCallback(
    () => ({
      locksLayer: locksLayerRef.current,
      docksLayer: docksLayerRef.current,
      pegelsLayer: pegelsLayerRef.current,
      worldHeritageLayer: worldHeritageLayerRef.current,
    }),
    []
  );

  return useMemo(
    () => ({
      isLocks,
      isDockingPoints,
      isPegelsVisible,
      isWorldHeritageSitesVisible,
      availablePegels,
      toggleLocks,
      toggleDocks,
      togglePegels,
      toggleWorldHeritageSites,
      showLocks,
      showDocks,
      showPegels,
      showWorldHeritageSites,
      setWorldHeritageActiveSelection,
      clearWorldHeritageActiveSelection,
      refreshDocks: showDocks,
      clearAll,
      getLayerRefs,
    }),
    [
      availablePegels,
      clearAll,
      getLayerRefs,
      isDockingPoints,
      isLocks,
      isPegelsVisible,
      isWorldHeritageSitesVisible,
      showDocks,
      showLocks,
      showPegels,
      showWorldHeritageSites,
      setWorldHeritageActiveSelection,
      clearWorldHeritageActiveSelection,
      toggleDocks,
      toggleLocks,
      togglePegels,
      toggleWorldHeritageSites,
    ]
  );
}
